import React, { useState, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Job } from '../types';
import { formatCurrency, formatPercent } from '../utils/helpers';
import { Trash2, CheckCircle, Clock, ArrowUpDown, FileSpreadsheet, Pencil } from 'lucide-react';
interface CommissionTableProps {
  jobs: Job[];
  onDeleteJob: (id: string) => void;
  onMarkDepositPaid: (id: string) => void;
  onEditJob: (job: Job) => void;
}
type SortField = 'sitDate' | 'closeDate' | 'sitPayDate' | 'commissionPayDate' | 'customerName' | 'projectTotal' | 'netAmount' | 'status';
type SortDirection = 'asc' | 'desc';
export function CommissionTable({
  jobs,
  onDeleteJob,
  onMarkDepositPaid,
  onEditJob
}: CommissionTableProps) {
  const [sortField, setSortField] = useState<SortField>('sitDate');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };
  // Normalize jobs for backward compatibility
  const normalizedJobs = jobs.map(job => ({
    ...job,
    sitDate: job.sitDate || job.dateOfSale || '',
    sitPayDate: job.sitPayDate || job.payDate || '',
    isClosed: job.isClosed ?? true,
    sitPay: job.sitPay ?? (job.setterName ? 150 : 0)
  }));
  const sortedJobs = [...normalizedJobs].sort((a, b) => {
    let valA = a[sortField] || '';
    let valB = b[sortField] || '';
    let comparison = 0;
    if (valA < valB) comparison = -1;
    if (valA > valB) comparison = 1;
    return sortDirection === 'asc' ? comparison : -comparison;
  });
  // Calculate Totals
  const totals = normalizedJobs.reduce((acc, job) => ({
    projectTotal: acc.projectTotal + (job.projectTotal || 0),
    netAmount: acc.netAmount + (job.netAmount || 0),
    sitPay: acc.sitPay + (job.sitPay || 0),
    closerCommission: acc.closerCommission + (job.closerCommission || 0),
    setterCommission: acc.setterCommission + (job.setterCommission || 0),
    totalCommission: acc.totalCommission + (job.sitPay || 0) + (job.closerCommission || 0) + (job.setterCommission || 0)
  }), {
    projectTotal: 0,
    netAmount: 0,
    sitPay: 0,
    closerCommission: 0,
    setterCommission: 0,
    totalCommission: 0
  });
  const SortIcon = ({
    field
  }: {
    field: SortField;
  }) => <ArrowUpDown className={`inline-block w-3 h-3 ml-1 transition-colors ${sortField === field ? 'text-indigo-600' : 'text-slate-300'}`} data-id="element-123" />;
  const handleExportCSV = () => {
    // Build payroll-friendly CSV rows
    const headers = ['Sit Date', 'Close Date', 'Sit Pay Date', 'Comm. Pay Date', 'Customer', 'Project Type', 'Project Total', 'Finance Company', 'Finance Rate', 'Finance Fee', 'Net Amount', 'Closer', 'Closer %', 'Closer Commission', 'Setter', 'Sit Pay', 'Setter Comm. %', 'Setter Commission', 'Deposit Paid', 'Status'];
    const rows = sortedJobs.map(job => [job.sitDate, job.closeDate || '-', job.sitPayDate, job.commissionPayDate || '-', job.customerName, job.isClosed ? job.projectType : '-', job.isClosed ? (job.projectTotal || 0).toFixed(2) : '-', job.isClosed ? job.financeCompany : '-', job.isClosed ? ((job.financeRate || 0) * 100).toFixed(2) + '%' : '-', job.isClosed ? (job.financeFee || 0).toFixed(2) : '-', job.isClosed ? (job.netAmount || 0).toFixed(2) : '-', job.isClosed ? job.closerName : '-', job.isClosed ? ((job.closerRate || 0) * 100).toFixed(2) + '%' : '-', job.isClosed ? (job.closerCommission || 0).toFixed(2) : '-', job.setterName || '', (job.sitPay || 0).toFixed(2), job.isClosed && job.setterName ? ((job.setterRate || 0) * 100).toFixed(2) + '%' : '-', job.isClosed && job.setterName ? (job.setterCommission || 0).toFixed(2) : '-', job.isClosed ? job.depositPaid ? 'Yes' : 'No' : '-', job.status]);
    const csvContent = [headers.join(','), ...rows.map(row => row.map(cell => `"${cell}"`).join(','))].join('\n');
    const blob = new Blob([csvContent], {
      type: 'text/csv'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `commissions-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  if (jobs.length === 0) {
    return <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-12 text-center" data-id="element-124">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-50 mb-4" data-id="element-125">
          <Clock className="w-8 h-8 text-slate-400" data-id="element-126" />
        </div>
        <h3 className="text-lg font-medium text-slate-900 mb-1" data-id="element-127">
          No jobs tracked yet
        </h3>
        <p className="text-slate-500" data-id="element-128">
          Enter a job above to start calculating commissions.
        </p>
      </div>;
  }
  return <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden flex flex-col" data-id="element-129">
      <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50 flex items-center justify-end" data-id="element-130">
        <button onClick={handleExportCSV} className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 hover:text-indigo-600 transition-colors" data-id="element-131">
          <FileSpreadsheet className="w-4 h-4" data-id="element-132" />
          Export CSV
        </button>
      </div>
      <div className="overflow-x-auto" data-id="element-133">
        <table className="w-full text-sm text-left whitespace-nowrap" data-id="element-134">
          <thead className="text-xs text-slate-600 uppercase bg-slate-50/80 border-b border-slate-200" data-id="element-135">
            <tr data-id="element-136">
              <th className="px-4 py-3 font-semibold cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => handleSort('sitDate')} data-id="element-137">
                Sit Date <SortIcon field="sitDate" data-id="element-138" />
              </th>
              <th className="px-4 py-3 font-semibold cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => handleSort('closeDate')} data-id="element-139">
                Close Date <SortIcon field="closeDate" data-id="element-140" />
              </th>
              <th className="px-4 py-3 font-semibold cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => handleSort('customerName')} data-id="element-141">
                Customer <SortIcon field="customerName" data-id="element-142" />
              </th>
              <th className="px-4 py-3 font-semibold" data-id="element-143">Project Total</th>
              <th className="px-4 py-3 font-semibold" data-id="element-144">Net Amount</th>
              <th className="px-4 py-3 font-semibold" data-id="element-145">Closer Comm.</th>
              <th className="px-4 py-3 font-semibold" data-id="element-146">Setter</th>
              <th className="px-4 py-3 font-semibold cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => handleSort('sitPayDate')} data-id="element-147">
                Sit Pay Date <SortIcon field="sitPayDate" data-id="element-148" />
              </th>
              <th className="px-4 py-3 font-semibold text-right" data-id="element-149">Sit Pay</th>
              <th className="px-4 py-3 font-semibold cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => handleSort('commissionPayDate')} data-id="element-150">
                Comm. Pay Date <SortIcon field="commissionPayDate" data-id="element-151" />
              </th>
              <th className="px-4 py-3 font-semibold text-right" data-id="element-152">
                Setter Comm.
              </th>
              <th className="px-4 py-3 font-semibold cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => handleSort('status')} data-id="element-153">
                Status <SortIcon field="status" data-id="element-154" />
              </th>
              <th className="px-4 py-3 font-semibold text-center" data-id="element-155">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-156">
            <AnimatePresence data-id="element-157">
              {sortedJobs.map(job => <motion.tr key={job.id} initial={{
              opacity: 0,
              bg: '#f8fafc'
            }} animate={{
              opacity: 1,
              bg: '#ffffff'
            }} exit={{
              opacity: 0,
              scale: 0.95
            }} className={`hover:bg-slate-50/50 transition-colors group ${!job.isClosed ? 'bg-slate-50/30' : ''}`} data-id="element-158">
                  <td className="px-4 py-3 text-slate-600" data-id="element-159">{job.sitDate}</td>
                  <td className="px-4 py-3 text-slate-600" data-id="element-160">
                    {job.closeDate || '-'}
                  </td>
                  <td className="px-4 py-3 font-medium text-slate-900" data-id="element-161">
                    {job.customerName}
                  </td>
                  <td className="px-4 py-3 text-slate-600" data-id="element-162">
                    {job.isClosed ? formatCurrency(job.projectTotal || 0) : '-'}
                  </td>
                  <td className="px-4 py-3 text-slate-600" data-id="element-163">
                    {job.isClosed ? formatCurrency(job.netAmount || 0) : '-'}
                  </td>
                  <td className="px-4 py-3 text-slate-600" data-id="element-164">
                    {job.isClosed ? <div className="flex flex-col" data-id="element-165">
                        <span className="font-medium text-emerald-600" data-id="element-166">
                          {formatCurrency(job.closerCommission || 0)}
                        </span>
                        <span className="text-xs text-slate-400" data-id="element-167">
                          {job.closerName} ({formatPercent(job.closerRate || 0)}
                          )
                        </span>
                      </div> : '-'}
                  </td>
                  <td className="px-4 py-3 font-medium text-slate-900" data-id="element-168">
                    {job.setterName}
                  </td>
                  <td className="px-4 py-3 text-slate-600" data-id="element-169">{job.sitPayDate}</td>
                  <td className="px-4 py-3 text-right font-medium text-emerald-600" data-id="element-170">
                    {formatCurrency(job.sitPay || 0)}
                  </td>
                  <td className="px-4 py-3 text-slate-600" data-id="element-171">
                    {job.commissionPayDate || '-'}
                  </td>
                  <td className="px-4 py-3 text-right" data-id="element-172">
                    {job.isClosed && job.setterName ? <div className="flex flex-col items-end" data-id="element-173">
                        <span className="font-medium text-emerald-600" data-id="element-174">
                          {formatCurrency(job.setterCommission || 0)}
                        </span>
                        <span className="text-xs text-slate-400" data-id="element-175">
                          {formatPercent(job.setterRate || 0)}
                        </span>
                      </div> : '-'}
                  </td>
                  <td className="px-4 py-3" data-id="element-176">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${job.status === 'Ready' ? 'bg-emerald-100 text-emerald-800' : job.status === 'Sit Only' ? 'bg-slate-100 text-slate-700' : 'bg-amber-100 text-amber-800'}`} data-id="element-177">
                      {job.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center" data-id="element-178">
                    <div className="flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-179">
                      <button onClick={() => onEditJob(job)} title="Edit Job" className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors" data-id="element-180">
                        <Pencil className="w-4 h-4" data-id="element-181" />
                      </button>
                      {job.status === 'Pending Deposit' && <button onClick={() => onMarkDepositPaid(job.id)} title="Mark Deposit Paid" className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-md transition-colors" data-id="element-182">
                          <CheckCircle className="w-4 h-4" data-id="element-183" />
                        </button>}
                      <button onClick={() => onDeleteJob(job.id)} title="Delete Job" className="p-1.5 text-red-500 hover:bg-red-50 rounded-md transition-colors" data-id="element-184">
                        <Trash2 className="w-4 h-4" data-id="element-185" />
                      </button>
                    </div>
                  </td>
                </motion.tr>)}
            </AnimatePresence>
          </tbody>
          <tfoot className="bg-slate-50 border-t border-slate-200 font-semibold text-slate-900" data-id="element-186">
            <tr data-id="element-187">
              <td colSpan={3} className="px-4 py-4 text-right" data-id="element-188">
                Totals:
              </td>
              <td className="px-4 py-4" data-id="element-189">
                {formatCurrency(totals.projectTotal)}
              </td>
              <td className="px-4 py-4" data-id="element-190">{formatCurrency(totals.netAmount)}</td>
              <td className="px-4 py-4 text-emerald-700" data-id="element-191">
                {formatCurrency(totals.closerCommission)}
              </td>
              <td data-id="element-192"></td>
              <td data-id="element-193"></td>
              <td className="px-4 py-4 text-right text-emerald-700" data-id="element-194">
                {formatCurrency(totals.sitPay)}
              </td>
              <td data-id="element-195"></td>
              <td className="px-4 py-4 text-right text-emerald-700" data-id="element-196">
                {formatCurrency(totals.setterCommission)}
              </td>
              <td colSpan={2} className="px-4 py-4 text-right text-emerald-700" data-id="element-197">
                Total Payout: {formatCurrency(totals.totalCommission)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>;
}